/**
 * `g` then a letter, for going anywhere.
 *
 * Single letters are the fastest thing an app can offer and there are only
 * twenty-six of them. Eleven destinations plus capture, search and the editing
 * keys would spend most of the alphabet on navigation, and then the next
 * feature that wants a key has nowhere to go. A prefix buys the whole set for
 * one letter.
 *
 * `t`, `i`, `c` and `/` stay as they are. The two screens you are on all day
 * keep a single keystroke, and the ones you visit on a Sunday do not — which
 * is the same reasoning that gave the phone five tabs rather than eleven.
 */

/** The letter that arms the chord. */
export const GO_PREFIX = 'g'

/**
 * How long the prefix stays armed.
 *
 * Long enough to be a deliberate two-key sequence, short enough that a `g`
 * you typed and forgot about does not turn the next letter into a navigation.
 * A stale prefix that never expires is the failure people describe as "it
 * randomly jumped somewhere".
 */
export const GO_TIMEOUT_MS = 1500

/**
 * Where each letter goes.
 *
 * First letter of the destination, and where two share one, the screen you
 * visit daily keeps it:
 *
 *   Today over Tasks, Routines over Review, Inbox over Insights,
 *   Habits over Help.
 *
 * The four that lost get a distinctive letter from their own name rather than
 * a number or an arbitrary neighbour — `k` in tas**k**s, `v` in re**v**iew,
 * `n` in i**n**sights — and Help takes `?`, which every app that has this
 * feature already uses for exactly this.
 */
export const GO_DESTINATIONS = {
  t: { to: '/', label: 'Today' },
  i: { to: '/inbox', label: 'Inbox' },
  k: { to: '/tasks', label: 'Tasks' },
  h: { to: '/habits', label: 'Habits' },
  r: { to: '/routines', label: 'Routines' },
  c: { to: '/chores', label: 'Chores' },
  l: { to: '/lists', label: 'Lists' },
  v: { to: '/review', label: 'Review' },
  n: { to: '/insights', label: 'Insights' },
  a: { to: '/archive', label: 'Archive' },
  s: { to: '/settings', label: 'Settings' },
  '?': { to: '/help', label: 'Help' },
} as const

export type GoKey = keyof typeof GO_DESTINATIONS

/** Armed at this instant, or not armed. */
export type GoState = { armedAt: number } | null

export type GoStep = {
  /** What the prefix state becomes. */
  state: GoState
  /** Where to navigate, if the chord completed. */
  to?: string
}

function isGoKey(key: string): key is GoKey {
  return Object.hasOwn(GO_DESTINATIONS, key)
}

/**
 * One keystroke, given where the chord had got to.
 *
 * Pure, and takes `now` rather than reading the clock, so every branch —
 * including expiry — can be tested without waiting or mocking time.
 *
 * The rules, in the order they matter:
 *
 *  - An armed prefix that has expired is treated as though it was never
 *    pressed, so the key is free to arm a new one.
 *  - While armed, a letter that names a destination goes there.
 *  - While armed, any other key cancels. Silently: a chord that half-matched
 *    and then did something else is worse than one that did nothing.
 *  - `g` while already armed re-arms rather than cancelling, because pressing
 *    it twice is a hesitation, not a new intention.
 */
export function stepGo(
  state: GoState,
  key: string,
  now: number,
  timeoutMs: number = GO_TIMEOUT_MS,
): GoStep {
  const armed = state !== null && now - state.armedAt < timeoutMs
  const lower = key.length === 1 ? key.toLowerCase() : key

  if (!armed) {
    return lower === GO_PREFIX ? { state: { armedAt: now } } : { state: null }
  }

  if (lower === GO_PREFIX) return { state: { armedAt: now } }

  if (isGoKey(lower)) {
    return { state: null, to: GO_DESTINATIONS[lower].to }
  }

  return { state: null }
}
